import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useIntl } from 'react-intl';

const OtherPrograms: React.FC = () => {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5))',
          backgroundColor: '#673ab7',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          height: '50vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h2" component="h1" gutterBottom>
              {t('otherPrograms.hero.title')}
            </Typography>
            <Typography variant="h5" gutterBottom>
              {t('otherPrograms.hero.subtitle')}
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Asset Allocation Section */}
      <Container sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" align="center" gutterBottom>
          {t('otherPrograms.assetAllocation.title')}
        </Typography>
        <Typography variant="h5" align="center" gutterBottom sx={{ mb: 4 }}>
          {t('otherPrograms.assetAllocation.subtitle')}
        </Typography>
        <Typography variant="body1" paragraph>
          {t('otherPrograms.assetAllocation.description')}
        </Typography>

        <Card sx={{ mb: 6 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('otherPrograms.assetAllocation.services')}
            </Typography>
            <List>
              {[
                'otherPrograms.assetAllocation.services.realEstate',
                'otherPrograms.assetAllocation.services.securities',
                'otherPrograms.assetAllocation.services.taxPlanning',
                'otherPrograms.assetAllocation.services.retirement',
                'otherPrograms.assetAllocation.services.business',
              ].map((item, index) => (
                <ListItem key={index}>
                  <ListItemText primary={t(item)} />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>

        <Typography variant="body1" paragraph>
          {t('otherPrograms.assetAllocation.compliance')}
        </Typography>
      </Container>

      {/* Corporate Overseas Services Section */}
      <Box sx={{ bgcolor: 'background.paper', py: 8 }}>
        <Container>
          <Typography variant="h3" component="h2" align="center" gutterBottom>
            {t('otherPrograms.corporateOverseas.title')}
          </Typography>
          <Typography variant="h5" align="center" gutterBottom sx={{ mb: 4 }}>
            {t('otherPrograms.corporateOverseas.subtitle')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('otherPrograms.corporateOverseas.description')}
          </Typography>

          <Card sx={{ mb: 6 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('otherPrograms.corporateOverseas.services')}
              </Typography>
              <List>
                {[
                  'otherPrograms.corporateOverseas.services.companySelection',
                  'otherPrograms.corporateOverseas.services.taxFiling',
                  'otherPrograms.corporateOverseas.services.licenses',
                  'otherPrograms.corporateOverseas.services.compliance',
                  'otherPrograms.corporateOverseas.services.business',
                ].map((item, index) => (
                  <ListItem key={index}>
                    <ListItemText primary={t(item)} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>

          <Typography variant="body1" paragraph>
            {t('otherPrograms.corporateOverseas.compliance')}
          </Typography>
        </Container>
      </Box>

      {/* Education Programs Section */}
      <Box sx={{ bgcolor: 'background.paper', py: 8 }}>
        <Container>
          <Typography variant="h3" component="h2" align="center" gutterBottom>
            {t('otherPrograms.education.title')}
          </Typography>
          <Typography variant="h5" align="center" gutterBottom sx={{ mb: 4 }}>
            {t('otherPrograms.education.subtitle')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('otherPrograms.education.description')}
          </Typography>

          <Card sx={{ mb: 6 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('otherPrograms.education.services')}
              </Typography>
              <List>
                {[
                  'otherPrograms.education.services.selection',
                  'otherPrograms.education.services.visa',
                  'otherPrograms.education.services.housing',
                  'otherPrograms.education.services.counseling',
                  'otherPrograms.education.services.cultural',
                  'otherPrograms.education.services.support',
                ].map((item, index) => (
                  <ListItem key={index}>
                    <ListItemText primary={t(item)} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>

          <Typography variant="body1" paragraph>
            {t('otherPrograms.education.conclusion')}
          </Typography>
        </Container>
      </Box>

      {/* Why Choose Us Section */}
      <Container sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" align="center" gutterBottom>
          {t('otherPrograms.whyChoose.title')}
        </Typography>
        <Typography variant="h5" align="center" gutterBottom sx={{ mb: 4 }}>
          {t('otherPrograms.whyChoose.subtitle')}
        </Typography>

        <Grid container spacing={4}>
          {[
            'otherPrograms.whyChoose.experience',
            'otherPrograms.whyChoose.team',
            'otherPrograms.whyChoose.security',
            'otherPrograms.whyChoose.peace',
          ].map((item, index) => (
            <Grid item xs={12} md={6} key={index}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card>
                  <CardContent>
                    <Typography variant="body1">
                      {t(item)}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        <Typography variant="h6" align="center" sx={{ mt: 6 }}>
          {t('otherPrograms.whyChoose.cta')}
        </Typography>
      </Container>
    </Box>
  );
};

export default OtherPrograms; 